/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { SubscriptionService } from '../../../services/subscription.service';
import type { UsageStats as UsageStatsType } from '../../../services/subscription.service';

interface UsageStatsProps {
  stats: UsageStatsType;
}

const containerStyles = css`
  background: rgba(139, 92, 246, 0.05);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 12px;
  padding: 24px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-sizing: border-box; min-width: 0; overflow: hidden;
  &:hover { border-color: rgba(139, 92, 246, 0.3); background: rgba(139, 92, 246, 0.08); }
  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.05); border-color: rgba(139, 92, 246, 0.25);
    &:hover { border-color: rgba(139, 92, 246, 0.35); background: rgba(139, 92, 246, 0.08); }
  }

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const headerStyles = css`
  margin-bottom: 24px;
  h3 { margin: 0 0 8px 0; font-size: 20px; color: #ffffff; font-weight: 600; }
  p { margin: 0; color: rgba(255, 255, 255, 0.7); font-size: 14px; }
  [data-theme="light"] & { h3 { color: #1F2933; } p { color: #6B7280; } }
`;

const usageItemStyles = css`margin-bottom: 24px; &:last-child { margin-bottom: 0; }`;

const usageHeaderStyles = css`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;
`;

const usageLabelStyles = css`
  font-size: 14px; font-weight: 600; color: #ffffff;
  [data-theme="light"] & { color: #1F2933; }
`;

const usageValueStyles = css`
  font-size: 14px; color: rgba(255, 255, 255, 0.8);
  [data-theme="light"] & { color: rgba(31, 41, 51, 0.8); }
`;

const progressBarContainerStyles = css`
  width: 100%; height: 8px; background: rgba(139, 92, 246, 0.1);
  border-radius: 4px; overflow: hidden;
  [data-theme="light"] & { background: rgba(139, 92, 246, 0.15); }
`;

const progressBarFillStyles = (percentage: number, color: string) => css`
  height: 100%; width: ${percentage}%; background: ${color};
  transition: width 0.3s ease; border-radius: 4px;
`;

const warningStyles = css`
  margin-top: 8px; padding: 8px 12px;
  background: rgba(255, 193, 7, 0.1); border-left: 3px solid rgba(255, 193, 7, 0.6);
  border-radius: 4px; font-size: 13px; color: rgba(255, 193, 7, 0.9);
  [data-theme="light"] & { background: rgba(217, 119, 6, 0.1); border-left-color: rgba(217, 119, 6, 0.6); color: #d97706; }
`;

const dangerStyles = css`
  margin-top: 8px; padding: 8px 12px;
  background: rgba(230, 57, 70, 0.1); border-left: 3px solid rgba(230, 57, 70, 0.6);
  border-radius: 4px; font-size: 13px; color: rgba(255, 92, 92, 0.9);
  [data-theme="light"] & { background: rgba(220, 38, 38, 0.1); border-left-color: rgba(220, 38, 38, 0.6); color: #dc2626; }
`;

const upgradeButtonStyles = css`
  margin-top: 20px; width: 100%; padding: 12px;
  background: rgba(139, 92, 246, 0.2); border: 2px solid rgba(139, 92, 246, 0.5);
  color: #ffffff; border-radius: 8px; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: all 0.3s ease;
  &:hover { transform: translateY(-2px); background: rgba(139, 92, 246, 0.3);
    border-color: rgba(139, 92, 246, 0.7); box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3); }
  [data-theme="light"] & { background: rgba(139, 92, 246, 0.1); border-color: rgba(139, 92, 246, 0.4); color: #1F2933;
    &:hover { background: rgba(139, 92, 246, 0.15); border-color: rgba(139, 92, 246, 0.6); } }
`;

export const UsageStats = ({ stats }: UsageStatsProps) => {
  const { t } = useTranslation();
  const { credits, plan } = stats;
  // Guard against backend inconsistency (remaining > total)
  const effectiveTotal = Math.max(credits.total, credits.remaining);
  const usagePercent = effectiveTotal > 0
    ? Math.round(((effectiveTotal - credits.remaining) / effectiveTotal) * 100)
    : 0;
  const color = SubscriptionService.getUsageColor(usagePercent);

  const getAlert = () => {
    if (credits.remaining === 0) {
      return { type: 'danger' as const, message: t('subscription.creditsExhausted') };
    }
    if (usagePercent >= 90) {
      return { type: 'danger' as const, message: t('subscription.creditsLowDanger', { percent: usagePercent, remaining: credits.remaining }) };
    }
    if (usagePercent >= 75) {
      return { type: 'warning' as const, message: t('subscription.creditsLowWarning', { percent: usagePercent, remaining: credits.remaining, total: effectiveTotal }) };
    }
    return null;
  };

  const alert = getAlert();

  return (
    <div css={containerStyles}>
      <div css={headerStyles}>
        <h3>{t('subscription.usageStats')}</h3>
        <p>{t('subscription.creditsPerCycle', { name: plan.name, credits: plan.creditsPerCycle })}</p>
      </div>

      <div css={usageItemStyles}>
        <div css={usageHeaderStyles}>
          <span css={usageLabelStyles}>{t('subscription.credits')}</span>
          <span css={usageValueStyles}>{credits.remaining} / {effectiveTotal}</span>
        </div>
        <div css={progressBarContainerStyles}>
          <div css={progressBarFillStyles(Math.min(usagePercent, 100), color)} />
        </div>
        {alert && (
          <div css={alert.type === 'danger' ? dangerStyles : warningStyles}>
            {alert.message}
          </div>
        )}
      </div>

      {usagePercent >= 75 && (
        <button css={upgradeButtonStyles} onClick={() => { window.location.hash = '#upgrade'; }}>
          {t('subscription.upgradeForMore')}
        </button>
      )}
    </div>
  );
};
